import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  CloudArrowUpIcon,
  EyeIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { TargetBox } from 'components/DragDrop'
import { PlainInput } from 'components/PlainInput'
import type { BaseFile } from 'config'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { usePermissions } from 'hooks/usePermissions'
import { docCategoryFor, docStatus } from 'pages/LoanSubmission/constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { downloadS3Document, openS3Document, uploadFiles, uploadMismoXmlFromFileKey } from 'services'
import { svgLoading } from 'stories/assets'
import { confirm, formatTime, hasFileExt } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { Checkbox } from '../Checkbox/Checkbox'
import { Select } from '../Select/Select'
import { Tooltip } from '../Tooltip/Tooltip'

export interface DocumentFile extends BaseFile {
  status: string
  category: string
  [key: string]: any
}

const documentSelectionKey = (document: DocumentFile | Record<string, any>) =>
  String(document?.fileKey || document?.key || document?.id || '')

interface InputFileTableProps {
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Tooltip of Input
   */
  tooltip?: string
  /**
   * Title of Input
   */
  title?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Error of Input
   */
  error?: string
  /**
   * Custom class name
   */
  className?: string

  filePath: string
  /**
   * Is has icon
   */
  hasIcon?: boolean
  /**
   * Required
   */
  required?: boolean
  /**
   * Icon component
   */
  icon?: string | JSX.Element | null

  multiple?: boolean

  acceptFileTypes?: string

  history?: boolean

  value?: DocumentFile[]

  showCategory?: boolean
  canEditCategory?: boolean

  showStatus?: boolean

  uploadable?: boolean

  disableNameChange?: boolean

  defaultCategory?: string

  narrow?: boolean
  categories?: Array<string>
  customFields?: Array<any>
  additionalActions?: JSX.Element[] | JSX.Element | null
  additionalRowActions?: JSX.Element | Function | null
  onChange: (e: DocumentFile[]) => void
  onBlur?: () => void
  showHistory?: () => void
  canDelete?: boolean

  onTitleClick?: () => void

  type?: string

  /** When true, show section + per-row checkboxes for bulk actions. */
  selectable?: boolean
  selectedKeys?: string[]
  onSelectionChange?: (keys: string[]) => void
}

export const renderFileList = (files: BaseFile[]) => {
  return (
    <div>
      {files.map((file) => (
        <p
          className="text-sm text-shade-blue hover:underline flex items-start gap-2 cursor-pointer"
          onClick={() => openS3Document(file.fileKey)}
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          <span className="flex-1">{file.name}</span>
        </p>
      ))}
    </div>
  )
}

export const InputFileTable = ({
  tooltip = '',
  title = '',
  name = '',
  error = '',
  className = '',
  filePath: _filePath = '',
  required,
  multiple,
  acceptFileTypes = 'application/pdf',
  history,
  value = [],
  showStatus = true,
  showCategory = true,
  canEditCategory = true,
  uploadable = true,
  disabled = false,
  disableNameChange = false,
  defaultCategory = 'TRAILING DOCS',
  narrow = false,
  categories = [],
  customFields = [],
  additionalActions = [],
  onChange = () => {},
  showHistory = () => {},
  additionalRowActions,
  canDelete = true,
  onTitleClick,
  type: inputType = '',
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
}: InputFileTableProps) => {
  if (!name) name = `file-input-${Date.now()}`
  if (!value || !Array.isArray(value)) value = []

  const isFannie34Xml = useMemo(() => {
    return (isCustomFieldKey(name) && acceptFileTypes == 'text/xml') || inputType == 'fannie34Xml'
  }, [name, acceptFileTypes])

  const isFannie34XmlRef = useRef(isFannie34Xml)
  isFannie34XmlRef.current = isFannie34Xml

  const rulesConfig = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.RulesConfig])
  const { fileExtensions: acceptedFileTypes } = rulesConfig

  const [isLoading, setLoading] = useState(false)
  const [filePath, setFilePath] = useState('')

  if (disabled) uploadable = false

  const { hasPermission } = usePermissions()
  const canChangeStatus = hasPermission('Global.FileTable.Status.Change')

  const sectionKeys = useMemo(
    () => value.map(documentSelectionKey).filter(Boolean),
    [value],
  )
  const selectedInSection = sectionKeys.filter((key) => selectedKeys.includes(key)).length
  const allSectionSelected = sectionKeys.length > 0 && selectedInSection === sectionKeys.length

  const onToggleDocument = (document: DocumentFile, checked: boolean) => {
    if (!onSelectionChange) return
    const key = documentSelectionKey(document)
    if (!key) return
    if (checked) onSelectionChange(selectedKeys.includes(key) ? selectedKeys : [...selectedKeys, key])
    else onSelectionChange(selectedKeys.filter((item) => item !== key))
  }

  const onToggleSection = (checked: boolean) => {
    if (!onSelectionChange || !sectionKeys.length) return
    const without = selectedKeys.filter((item) => !sectionKeys.includes(item))
    onSelectionChange(checked ? [...without, ...sectionKeys] : without)
  }

  useEffect(() => {
    setLoading(false)
    if (_filePath) setFilePath(_filePath)
    else {
      const loanNumber = setLoanNumber()
      setFilePath(`loan/${loanNumber}/document`)
    }
  }, [_filePath])

  const onDropDocument = async (document: any) => {
    if (document.files) {
      uploadDocumentFiles(document.files)
      return
    }

    const droppedDocs: any[] = Array.isArray(document.documents)
      ? document.documents
      : [document]

    const temp = cloneDeep(value)
    let changed = false
    droppedDocs.forEach((doc) => {
      const fileKey = doc.fileKey || doc.key
      if (!fileKey) return
      const exists = temp.some((item) => item.fileKey === fileKey)
      if (exists) return
      temp.push({
        ...doc,
        fileKey,
        name: doc.name,
        status: showStatus ? doc.status : undefined,
        category: showCategory ? doc.category : undefined,
        createdAt: doc.createdAt || Date.now(),
      })
      changed = true
    })
    if (changed) await onChange(temp)
  }

  const onUploadNewfile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    if (!files || files.length == 0) return
    const arrFile: Array<File> = []
    for (let i = 0; i < files.length; i++) arrFile.push(files[i])

    uploadDocumentFiles(arrFile)
  }

  const uploadDocumentFiles = (files: File[]): Promise<any> => {
    return new Promise((resolve) => {
      const data = {
        path: filePath,
      }
      let acceptedFiles: any = []
      const rejectedNames: string[] = []
      files.map((file) => {
        const { name } = file
        const params = name.toLowerCase().split('.')
        const length = params.length
        const ext = params[length - 1]
        if (acceptedFileTypes.includes(ext)) {
          acceptedFiles.push(file)
        } else {
          rejectedNames.push(name)
        }
      })
      // One toast per file buries the screen when a whole folder is dropped, so say it once.
      if (rejectedNames.length == 1)
        toast(`${rejectedNames[0]} is not ${acceptedFileTypes.join(', ')}`, { type: 'error' })
      else if (rejectedNames.length)
        toast(`${rejectedNames.length} files were skipped. Only ${acceptedFileTypes.join(', ')} are accepted.`, {
          type: 'error',
        })
      // Nothing usable was dropped, so don't put the table behind a spinner for an empty upload.
      if (!acceptedFiles.length) return resolve(false)

      setLoading(true)

      uploadFiles(data, acceptedFiles)
        .then(async (keys: Array<string>) => {
          const newData = cloneDeep(value)
          const newDocs: DocumentFile[] = []
          const id = Date.now()
          keys.forEach((key, index) => {
            newDocs.push({
              id: id + index,
              name: acceptedFiles[index].name,
              fileKey: key,
              status: 'Not Reviewed',
              category: showCategory
                ? categories.length === 1
                  ? categories[0]
                  : categories.length
                  ? ''
                  : defaultCategory
                : ' ',
              createdAt: Date.now(),
            })
          })
          newData.push(...newDocs)
          setLoading(false)
          await onChange(newData)

          if (isFannie34XmlRef.current && newDocs.length > 0) {
            const doc = newDocs[0]
            // for (const doc of newDocs) {
            const ok = await confirm(
              'Are you sure you want to override the existing loan information with the data from the 3.4 Mismo XML?',
            )
            if (ok) {
              try {
                const res = await uploadMismoXmlFromFileKey(doc.fileKey)
                if (res?.success) {
                  toast('Fannie 3.4 XML imported successfully.', { type: 'success' })
                  window.location.reload()
                } else {
                  toast(res?.message || 'Failed to import Fannie 3.4 XML.', { type: 'error' })
                }
              } catch {
                toast('Failed to import Fannie 3.4 XML.', { type: 'error' })
              }
            }
            // }
          }

          resolve(true)
        })
        .catch(() => {
          // The whole batch fails together. Drop the overlay so the table stays usable instead of
          // leaving it stuck behind the loading mask.
          setLoading(false)
          resolve(false)
        })
    })
  }

  const onUpdateDocumentProp = async (
    type: 'name' | 'status' | 'category' | string,
    index: number,
    newName: string,
  ) => {
    const newData = cloneDeep(value)
    if (type == 'name') {
      const orgName = newData[index][type]
      newName = newName + (hasFileExt(orgName) ? orgName.substring(orgName.length - 4) : '')
    }
    newData[index][type] = newName
    await onChange(newData)
  }

  const onRemoveDocument = async (document: DocumentFile, index: number) => {
    const content = (
      <div className="text-gray-900 mb-4 text-md">
        Are you sure want to delete this document?
        <div className="text-gray-800 flex flex-col text-left text-sm">
          <span>Document Name: {document.name}</span>
          <span>Uploaded Date: {formatTime(new Date(document.createdAt))}</span>
        </div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(value)
    newData.splice(index, 1)
    await onChange(newData)
  }

  const renderContent = () => {
    return (
      <div className={`relative group border-b`}>
        <div className="relative w-full rounded p-2 text-sm text-left">
          {uploadable && (
            <div className="absolute top-1 right-[calc(50%-135px)] w-full h-fit">
              <div className="absolute top-0 right-0 border-dashed border-2 border-gray-300 px-4 pt-1 text-gray-300 w-[270px] text-center">
                <CloudArrowUpIcon className="w-10 h-10 mx-auto" />
                <span>Drag and drop files.</span>
              </div>
            </div>
          )}
          <div className="flex border-b pb-1">
            <p className="flex-auto">
              {onTitleClick ? (
                <button
                  type="button"
                  className="font-semibold text-shade-blue hover:underline cursor-pointer"
                  onClick={onTitleClick}
                >
                  {title}
                </button>
              ) : (
                <span className="font-semibold">{title}</span>
              )}
              {required && '*'}
              <span className="ml-1 font-semibold">({value.length})</span>
              {tooltip.length > 0 ? <Tooltip message={tooltip}></Tooltip> : null}
              {history && (
                <span className="ml-1 hidden group-hover:inline-block" onClick={() => showHistory()}>
                  <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
                </span>
              )}{' '}
            </p>
            {isLoading && <img src={svgLoading} className="inline w-5 h-5 text-white animate-spin" />}

            {selectable && (
              <span className="p-1 mr-1" title="Select all in this section">
                <Checkbox
                  id={`filetable-section-${name}`}
                  title=""
                  disabled={!sectionKeys.length}
                  value={allSectionSelected}
                  onChange={onToggleSection}
                />
              </span>
            )}
            {additionalActions}
            {uploadable && (
              <>
                <label className="text-gray-700 p-1 hover-shadow1 cursor-pointer rounded" htmlFor={name}>
                  <ArrowUpTrayIcon className="w-4 h-4"></ArrowUpTrayIcon>
                </label>

                <input
                  className="hidden"
                  id={name}
                  type="file"
                  accept={acceptFileTypes}
                  multiple={multiple}
                  required
                  onChange={(event) => onUploadNewfile(event)}
                />
              </>
            )}
          </div>
          <div className="overflow-auto">
            <table className="w-full text-[13px]">
              <thead className="font-medium text-gray-600 bg-gray-100">
                {!narrow ? (
                  <tr>
                    <th className="px-2 py-1">No</th>
                    <th className="px-2 py-1">Document Name</th>
                    {showStatus && <th className="px-2">Status</th>}
                    {showCategory && <th className="px-2">Category</th>}
                    {customFields.map((field) => {
                      return (
                        <th key={field.key} className="px-2">
                          {field.title}
                        </th>
                      )
                    })}
                    <th className="px-2">Uploaded Date</th>
                    <th className="px-2">Actions</th>
                  </tr>
                ) : (
                  <>
                    <tr>
                      <th className="px-2 py-1" rowSpan={2}>
                        No
                      </th>
                      <th className="px-2 py-1" colSpan={Number(showStatus) + Number(showCategory) + 1}>
                        Document Name
                      </th>
                      <th className="px-2" rowSpan={2}>
                        Actions
                      </th>
                    </tr>
                    <tr>
                      {showStatus && <th className="px-2">Status</th>}
                      {showCategory && <th className="px-2">Category</th>}
                      {customFields.map((field) => {
                        return (
                          <th key={field.key} className="px-2">
                            {field.title}
                          </th>
                        )
                      })}
                      <th className="px-2">Uploaded Date</th>
                    </tr>
                  </>
                )}
              </thead>
              <tbody className="text-gray-900 text-[14px]">
                {value.map((document, index: number) => {
                  const cellName =
                    disabled || disableNameChange ? (
                      <span className="hover:underline cursor-pointer" onClick={() => openS3Document(document.fileKey)}>
                        {document.name}
                      </span>
                    ) : (
                      <PlainInput
                        value={
                          hasFileExt(document.name)
                            ? document.name.substring(0, document.name.length - 4)
                            : document.name
                        }
                        content={document.name}
                        onChange={(newName: string) => onUpdateDocumentProp('name', index, newName)}
                      />
                    )

                  const cellStatus =
                    showStatus && canChangeStatus ? (
                      <Select
                        id={`side-document-category-${document.id}`}
                        size={3}
                        className="-mb-4 bg-white"
                        options={docStatus}
                        value={document.status}
                        onChange={(value) => onUpdateDocumentProp('status', index, value)}
                      />
                    ) : (
                      document.status
                    )

                  const cellCategory = canEditCategory ? (
                    <Select
                      id={`side-document-category-${document.id}`}
                      size={3}
                      className="-mb-4 bg-white"
                      options={docCategoryFor(document.category, categories.length ? categories : undefined).options}
                      value={docCategoryFor(document.category, categories.length ? categories : undefined).value}
                      hasDefaultOption
                      onChange={(value) => onUpdateDocumentProp('category', index, value)}
                      sort
                    />
                  ) : (
                    <>{document.category}</>
                  )

                  const cellCreatedAt = formatTime(new Date(document.createdAt))

                  const selectionKey = documentSelectionKey(document)
                  const cellAction = (
                    <>
                      {selectable && (
                        <span className="p-1" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            id={`filetable-doc-${name}-${selectionKey || index}`}
                            title=""
                            value={selectedKeys.includes(selectionKey)}
                            onChange={(checked) => onToggleDocument(document, checked)}
                          />
                        </span>
                      )}
                      {additionalRowActions !== null &&
                        (typeof additionalRowActions == 'function'
                          ? additionalRowActions(document, index, onUpdateDocumentProp)
                          : additionalRowActions)}
                      <button
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer rounded"
                        onClick={() => openS3Document(document.fileKey)}
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer rounded"
                        onClick={() => downloadS3Document(document.fileKey, document.name)}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                      {(showCategory ||
                        !showStatus ||
                        document.status == 'Not Reviewed' ||
                        !document.status) &&
                        uploadable &&
                        canDelete && (
                          <button
                            className="text-red-800 p-1 hover-shadow1 cursor-pointer rounded"
                            onClick={() => onRemoveDocument(document, index)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                    </>
                  )

                  const customFieldContents = customFields.map((field) => {
                    return field.content(document, (value: any) => onUpdateDocumentProp(field.key, index, value))
                  })

                  return !narrow ? (
                    <tr className={`hover:bg-slate-100 ${index % 2 && 'bg-gray-50'}`} key={index}>
                      <td className="px-2">{index + 1}</td>
                      <td className="px-2">{cellName}</td>
                      {showStatus && <td className="px-2 w-36">{cellStatus}</td>}
                      {showCategory && <td className="px-2 w-40">{cellCategory}</td>}
                      {customFieldContents.map((v) => {
                        return <td className="px-2 w-36">{v}</td>
                      })}
                      <td className="px-2 w-36">{cellCreatedAt}</td>
                      <td className="px-2 w-28 py-1 flex items-center">{cellAction}</td>
                    </tr>
                  ) : (
                    <>
                      <tr className={`${index % 2 && 'bg-gray-50'}`} key={`${index}-1`}>
                        <td className="px-2" rowSpan={2}>
                          {index + 1}
                        </td>
                        <td className="px-2" colSpan={Number(showStatus) + Number(showCategory) + 1}>
                          {cellName}
                        </td>
                        <td className="px-2 w-20 py-1" rowSpan={2}>
                          {cellAction}
                        </td>
                      </tr>
                      <tr className={`${index % 2 && 'bg-gray-50'}`} key={`${index}-2`}>
                        {showStatus && <td className="px-2 w-36">{cellStatus}</td>}
                        {showCategory && <td className="px-2 w-36">{cellCategory}</td>}
                        {customFieldContents.map((v) => {
                          return <td className="px-2 w-36">{v}</td>
                        })}
                        <td className="px-2 w-36">{cellCreatedAt}</td>
                      </tr>
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  if (!uploadable)
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        {renderContent()}
        {error && <p className="peer-invalid:visible text-rose-700 text-xs pl-1 mt-2">{error}</p>}
      </div>
    )

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`flex flex-col gap-2 ${className}`}>
        <TargetBox types={['document', NativeTypes.FILE]} onDrop={(document: any) => onDropDocument(document)}>
          {renderContent()}
        </TargetBox>
        {error && <p className="peer-invalid:visible text-rose-700 text-xs pl-1 mt-2">{error}</p>}
      </div>
    </DndProvider>
  )
}
